import isEqual from "lodash.isequal";
import LZString from "lz-string";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isObjectNullOrEmpty } from "utils/helpers";

const channel = new BroadcastChannel("redux-sync");

const useCachedStateData = (
    storageKey,
    fetchAction,
    actionParams = null,
    stateValue,
    setReduxState,
    clearReduxState = null,
    isFeatureFlagEnabled = false
) => {
    const dispatch = useDispatch();
    const hasFetched = useRef(false);
    const prevActionParams = useRef(actionParams);
    const lastUpdatedByChannel = useRef(false);

    const data = useSelector((state) =>
        stateValue.split(".").reduce((acc, part) => acc?.[part], state)
    );

    useEffect(() => {
        const cachedData = localStorage.getItem(storageKey);

        if (cachedData) {
            dispatch(setReduxState(JSON.parse(LZString.decompress(cachedData))));
        } else if (fetchAction && isFeatureFlagEnabled && !hasFetched.current) {
            dispatch(fetchAction(actionParams));
            hasFetched.current = true;
        }

        const handleMessage = (event) => {
            if (
                event.data?.storageKey === storageKey &&
                !isObjectNullOrEmpty(event.data.data) &&
                !isEqual(event.data.data, data)
            ) {
                lastUpdatedByChannel.current = true;
                dispatch(setReduxState(event.data.data));
            }
        };

        channel.addEventListener('message', handleMessage);
        return () => channel.removeEventListener('message', handleMessage);
    }, []);

    useEffect(() => {
        if (fetchAction && !isEqual(actionParams, prevActionParams.current)) {
            prevActionParams.current = actionParams;
            hasFetched.current = false;
            dispatch(fetchAction(actionParams));
        }
    }, [actionParams]);

    useEffect(() => {
        if (isObjectNullOrEmpty(data)) return;

        const compressedData = LZString.compress(JSON.stringify(data));
        const cachedData = localStorage.getItem(storageKey);

        if (lastUpdatedByChannel.current) {
            lastUpdatedByChannel.current = false;
            return;
        }

        if (!cachedData || !isEqual(LZString.decompress(cachedData), data)) {
            localStorage.setItem(storageKey, compressedData);
            channel.postMessage({ storageKey, data });
        }
    }, [data]);

    useEffect(() => {
        const cleanup = () => {
            setTimeout(() => {
                if (!document.hasFocus()) {
                    localStorage.removeItem(storageKey);
                    if (clearReduxState) dispatch(clearReduxState());
                }
            }, 1000);
        };

        window.addEventListener('beforeunload', cleanup);
        return () => window.removeEventListener('beforeunload', cleanup);
    }, [dispatch]);

    return data;
};

export default useCachedStateData;